<template>
  <div class="plan-section">
    <div v-if="isFreeTrialActive" class="plan-section__content plan-section__content--trial">
      <div class="plan-section__row">
        <span class="plan-section__badge plan-section__badge--trial">
          <Icon icon="mdi:gift-outline" width="12" height="12" />
          Free Trial
        </span>
        <span class="plan-section__days">{{ daysRemaining }}d left</span>
      </div>
      <div class="plan-section__progress">
        <div
          class="plan-section__progress-fill"
          :class="{ 'plan-section__progress-fill--warning': daysRemaining <= 3 }"
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
      <button class="plan-section__btn plan-section__btn--primary" @click="handleUpgrade">
        <Icon icon="mdi:arrow-up-circle-outline" width="14" height="14" />
        Upgrade to paid plan
      </button>
    </div>

    <div v-else-if="isViewOnlyMode" class="plan-section__content plan-section__content--expired">
      <span class="plan-section__badge plan-section__badge--expired">
        <Icon icon="mdi:alert" width="12" height="12" />
        Trial Expired
      </span>
      <button class="plan-section__btn plan-section__btn--primary" @click="handleUpgrade">
        <Icon icon="mdi:rocket-launch" width="14" height="14" />
        Upgrade Now
      </button>
    </div>

    <div v-else-if="isPaidSubscriber" class="plan-section__content plan-section__content--pro">
      <button class="plan-section__btn plan-section__btn--manage" @click="handleManageBilling">
        <Icon icon="mdi:credit-card-outline" width="14" height="14" />
        Manage billing
      </button>
    </div>

    <div v-else class="plan-section__content">
      <button
        class="plan-section__btn plan-section__btn--primary plan-section__btn--full plan-section__btn--normal"
        @click="handleUpgrade"
      >
        <Icon icon="mdi:arrow-up-circle-outline" width="14" height="14" />
        Upgrade to paid plan
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Icon } from "@/components/ui";
import { SETTINGS_BILLING_ROUTE } from "@/router/routes";

const emit = defineEmits<{ (e: "navigate"): void }>();
const store = useStore();
const router = useRouter();

const trialStatus = computed(() => store.getters.trialStatus?.data);
const subscriptionStatus = computed(() => store.getters.subscriptionStatus?.data);
const isFreeTrialActive = computed(() => store.getters.isFreeTrialActive);
const isViewOnlyMode = computed(() => store.getters.isViewOnlyMode);

const isPaidSubscriber = computed(() => {
  const subStatus = subscriptionStatus.value?.status;
  if (subStatus === "active" || subStatus === "trialing") {
    return true;
  }
  const trial = trialStatus.value;
  return trial?.type === "stripe_subscription" && ["active", "trialing"].includes(trial?.status);
});

const daysRemaining = computed(() => trialStatus.value?.daysRemaining ?? 0);

const TRIAL_DURATION_DAYS = 14;
const progressPercent = computed(() => {
  const daysUsed = TRIAL_DURATION_DAYS - daysRemaining.value;
  return Math.min(100, Math.max(0, (daysUsed / TRIAL_DURATION_DAYS) * 100));
});

const handleUpgrade = () => {
  emit("navigate");
  router.push({ name: SETTINGS_BILLING_ROUTE });
};
const handleManageBilling = () => {
  emit("navigate");
  router.push({ name: SETTINGS_BILLING_ROUTE });
};
</script>

<style scoped>
.plan-section {
  border-bottom: 1px solid #e5e7eb;
}

.plan-section__content {
  padding: 10px 12px;
}

.plan-section__content--trial {
  background: linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%);
}

.plan-section__content--expired {
  background: linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%);
}

.plan-section__content--pro {
  background: #ffffff;
  padding: 8px 12px;
}

.plan-section__btn--manage {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.plan-section__btn--manage:hover {
  background: #e5e7eb;
  color: #111827;
}

.plan-section__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.plan-section__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.plan-section__badge--trial {
  background: #3e3efe;
  color: #fff;
}

.plan-section__badge--expired {
  background: #ef4444;
  color: #fff;
  margin-bottom: 8px;
}

.plan-section__badge--pro {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #fff;
}

.plan-section__days {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
}

.plan-section__progress {
  height: 3px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 10px;
}

.plan-section__progress-fill {
  height: 100%;
  background: #3e3efe;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.plan-section__progress-fill--warning {
  background: #f59e0b;
}

.plan-section__btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.plan-section__btn--normal {
  font-weight: 400;
}

.plan-section__btn--primary {
  background: #3e3efe;
  color: #fff;
}

.plan-section__btn--primary:hover {
  background: #3232e6;
}

.plan-section__btn--secondary {
  background: rgba(0, 0, 0, 0.08);
  color: #374151;
}

.plan-section__btn--secondary:hover {
  background: rgba(0, 0, 0, 0.12);
}
</style>
